import { useEffect, useRef, memo } from "react";

/* ── Simplified Perlin-like noise ──────────────────────────── */
const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
const lerp = (a, b, t) => a + t * (b - a);

// Hash-based gradient noise
const permutation = Array.from({ length: 256 }, (_, i) => i);
for (let i = 255; i > 0; i--) {
  const j = Math.floor(Math.random() * (i + 1));
  [permutation[i], permutation[j]] = [permutation[j], permutation[i]];
}
const perm = [...permutation, ...permutation];

const grad1d = (hash, x) => {
  return (hash & 1) === 0 ? x : -x;
};

const noise1d = (x) => {
  const xi = Math.floor(x) & 255;
  const xf = x - Math.floor(x);
  const u = fade(xf);
  return lerp(grad1d(perm[xi], xf), grad1d(perm[xi + 1], xf - 1), u);
};

const noise2d = (x, y) => {
  return (
    noise1d(x + y * 57.0) * 0.5 +
    noise1d(x * 0.5 + y * 31.0) * 0.3 +
    noise1d(x * 0.25 + y * 13.0) * 0.2
  );
};

/* ── Terrain config ────────────────────────────────────────── */
const COLS = 80;
const ROWS = 28;
const SPACING = 22;

const DigitalTerrain = () => {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let offset = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.parentElement?.getBoundingClientRect() || {
        width: window.innerWidth,
        height: 280,
      };
      canvas.width = rect.width * dpr;
      canvas.height = 280 * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = "280px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();

    const project = (x, y, z, width, height) => {
      // Simple perspective projection
      const fov = 300;
      const viewDist = 400;
      const scale = fov / (viewDist + z);
      return {
        x: width / 2 + x * scale,
        y: height * 0.3 + y * scale + z * 0.35,
        scale,
      };
    };

    const animate = () => {
      offset += 0.008;
      const width = canvas.width / (Math.min(window.devicePixelRatio || 1, 2));
      const height = 280;

      ctx.clearRect(0, 0, width, height);

      const points = [];

      // Generate terrain grid
      for (let row = 0; row < ROWS; row++) {
        points[row] = [];
        for (let col = 0; col < COLS; col++) {
          const x = (col - COLS / 2) * SPACING;
          const z = row * SPACING;

          // Multi-octave noise for natural-looking terrain
          const nx = col * 0.08 + offset;
          const ny = row * 0.12;
          const heightVal =
            noise2d(nx, ny) * 45 +
            noise2d(nx * 2, ny * 2) * 20 +
            Math.sin(col * 0.15 + offset * 3) * 8;

          // Fade edges
          const edgeFade =
            Math.min(col, COLS - 1 - col, 8) / 8 *
            Math.min(row, ROWS - 1 - row, 6) / 6;

          const y = -heightVal * edgeFade;

          const projected = project(x, y, z, width, height);
          points[row][col] = { ...projected, height: heightVal * edgeFade };
        }
      }

      // Draw horizontal lines (rows)
      for (let row = 0; row < ROWS; row++) {
        ctx.beginPath();
        for (let col = 0; col < COLS; col++) {
          const p = points[row][col];
          if (col === 0) {
            ctx.moveTo(p.x, p.y);
          } else {
            ctx.lineTo(p.x, p.y);
          }
        }
        const rowOpacity = Math.max(0.08, 1 - row / ROWS) * 0.6;
        const heightBrightness = Math.min(1, 0.3 + (points[row][Math.floor(COLS / 2)]?.height || 0) / 60);

        ctx.strokeStyle = `rgba(124, 77, 255, ${rowOpacity * heightBrightness})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      // Draw vertical lines (cols)
      for (let col = 0; col < COLS; col++) {
        ctx.beginPath();
        for (let row = 0; row < ROWS; row++) {
          const p = points[row][col];
          if (row === 0) {
            ctx.moveTo(p.x, p.y);
          } else {
            ctx.lineTo(p.x, p.y);
          }
        }
        const colOpacity = Math.max(0.05, Math.min(col, COLS - 1 - col, 10) / 10) * 0.4;
        ctx.strokeStyle = `rgba(39, 184, 255, ${colOpacity})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }

      // Draw mesh points at intersections (sparse)
      for (let row = 0; row < ROWS; row += 2) {
        for (let col = 0; col < COLS; col += 3) {
          const p = points[row][col];
          if (!p) continue;
          const pulse = Math.sin(offset * 4 + row * 0.3 + col * 0.2) * 0.5 + 0.5;
          const pointOpacity = (1 - row / ROWS) * 0.5 * pulse;

          if (pointOpacity > 0.05) {
            // Glow
            const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 6);
            grad.addColorStop(0, `rgba(124, 77, 255, ${pointOpacity * 0.6})`);
            grad.addColorStop(1, "transparent");
            ctx.beginPath();
            ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
            ctx.fillStyle = grad;
            ctx.fill();

            // Dot
            ctx.beginPath();
            ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(39, 184, 255, ${pointOpacity})`;
            ctx.fill();
          }
        }
      }

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    window.addEventListener("resize", resize);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-[5]" style={{ height: 280 }}>
      {/* Top fade */}
      <div
        className="absolute inset-x-0 top-0 z-10"
        style={{
          height: 80,
          background: "linear-gradient(to bottom, #060B1F, transparent)",
        }}
      />
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
};

export default memo(DigitalTerrain);
